import { useEffect, useState } from "react";
import { Center, Text3D } from "@react-three/drei";
import { useAtomValue, useSetAtom } from "jotai";
import { useLocation } from "wouter";
import { backToMainMenuOpenAtom, clientAtom, gamePhaseAtom, hostAtom, languageAtom } from "../../GlobalState.jsx";
import { connectSocket, disconnectSocket, getSocket } from "../../socket.js";
import { trackEvent } from '../../api';

const socket = getSocket();

function fontFor(language) {
  return language === "korean"
    ? "/fonts/Maplestory_Bold.json"
    : language === "chinese"
      ? "/fonts/Xiao Xiangjiao font_Regular.json"
      : "/fonts/Luckiest Guy_Regular.json";
}

function BoxButton({ children, icon = false, color = "orange", hoverColor = "green", onClick, position = [0, 0, 0], scale = [2, 0.65], font, textSize = 0.32, leftAligned = false }) {
  const [hover, setHover] = useState(false);
  const buttonColor = hover ? hoverColor : color;

  function handlePointerEnter(event) {
    event.stopPropagation();
    setHover(true);
    document.body.style.cursor = "pointer";
  }

  function handlePointerLeave(event) {
    event.stopPropagation();
    setHover(false);
    document.body.style.cursor = "default";
  }

  function handlePointerUp(event) {
    event.stopPropagation();
    onClick();
  }

  return <group position={position}>
    <mesh name="background-outer" scale={[scale[0], 0.01, scale[1]]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color={buttonColor} />
    </mesh>
    <mesh name="background-inner" scale={[scale[0] - 0.1, 0.02, scale[1] - 0.1]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="black" />
    </mesh>
    <mesh
      name="wrapper"
      scale={[scale[0], 0.03, scale[1]]}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerUp={handlePointerUp}
    >
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial transparent opacity={0} />
    </mesh>
    {children && <Center left={leftAligned} position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <Text3D font={font} size={textSize} height={0.01}>
        {children}
        <meshStandardMaterial color={buttonColor} />
      </Text3D>
    </Center>}
  </group>;
}

export function BackToMainMenuButton({ position, width = 1, height = 1 }) {
  const setConfirmationOpen = useSetAtom(backToMainMenuOpenAtom);
  const language = useAtomValue(languageAtom);
  const gamePhase = useAtomValue(gamePhaseAtom);

  const surface = gamePhase === 'lobby'
    ? 'lobby'
    : gamePhase === 'finished'
      ? 'end_scene'
      : gamePhase === 'minigame'
        ? 'minigame'
        : 'game';

  function openConfirmation() {
    setConfirmationOpen(true)
  }

  return <group position={position}>
    <BoxButton scale={[width, height]} font={fontFor(language)} textSize={0.3} onClick={openConfirmation}>
      {`BACK TO MAIN MENU`}
    </BoxButton>
  </group>;
}

function ConfirmationModal({ isHost, onCancel, onConfirm, position, font }) {
  return <group position={[position[0], position[1]+5, position[2]-1.5]}>
    <mesh name="background-outer" scale={[10, 0.01, 3.8]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="#f5f06a" />
    </mesh>
    <mesh name="background-inner" scale={[9.9, 0.02, 3.7]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="black" />
    </mesh>
    <Text3D font={font} position={[-4.7, 0.03, -1.2]} rotation={[-Math.PI / 2, 0, 0]} size={0.4} height={0.01}>
      BACK TO MAIN MENU
      <meshStandardMaterial color="#f5f06a" />
    </Text3D>
    <group position={[-4.7, 0.03, -0.35]} rotation={[-Math.PI / 2, 0, 0]}>
      <Text3D font={font} size={0.45} height={0.01}>
        {isHost ? "ARE YOU SURE? THE ROOM WILL\nCLOSE." : "ARE YOU SURE? YOU WILL\nLEAVE THE ROOM"}
        <meshStandardMaterial color="#f5f06a" />
      </Text3D>
    </group>
    <BoxButton position={[-2.4, 0.03, 1.25]} scale={[4.7, 0.75]} color="#f5f06a" font={font} onClick={onConfirm}>YES</BoxButton>
    <BoxButton position={[2.4, 0.03, 1.25]} scale={[4.7, 0.75]} color="red" hoverColor="green" font={font} onClick={onCancel}>CANCEL</BoxButton>
  </group>;
}

function RoomClosedModal({ onReturn, position, font }) {
  return <group position={position}>
    <mesh name="background-outer" scale={[8, 0.01, 2.3]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="orange" />
    </mesh>
    <mesh name="background-inner" scale={[7.9, 0.02, 2.2]}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="black" />
    </mesh>
    <Center position={[0, 0.03, -0.5]} rotation={[-Math.PI / 2, 0, 0]}>
      <Text3D font={font} size={0.42} height={0.01}>
        THE HOST CLOSED THE ROOM.
        <meshStandardMaterial color="orange" />
      </Text3D>
    </Center>
    <BoxButton position={[0, 0.03, 0.45]} scale={[4.5, 0.8]} font={font} textSize={0.3} onClick={onReturn}>MAIN MENU</BoxButton>
  </group>;
}

export default function BackToMainMenu({ confirmationModalPosition, confirmationModalScale, roomClosedModalPosition, roomClosedModalScale }) {
  const language = useAtomValue(languageAtom);
  const host = useAtomValue(hostAtom);
  const client = useAtomValue(clientAtom);
  const [_location, setLocation] = useLocation();
  const confirmationOpen = useAtomValue(backToMainMenuOpenAtom);
  const setConfirmationOpen = useSetAtom(backToMainMenuOpenAtom);
  const gamePhase = useAtomValue(gamePhaseAtom);
  const setGamePhase = useSetAtom(gamePhaseAtom);
  const [roomClosed, setRoomClosed] = useState(false);
  const isHost = host && client && host.socketId === client.socketId;
  const font = fontFor(language);
  const surface = gamePhase === 'lobby'
    ? 'lobby'
    : gamePhase === 'finished'
      ? 'end_scene'
      : gamePhase === 'minigame'
        ? 'minigame'
        : 'game';

  useEffect(() => {
    function handleRoomClosed() {
      setConfirmationOpen(false);
      setRoomClosed(true);
    }

    socket.on("roomClosed", handleRoomClosed);
    return () => socket.off("roomClosed", handleRoomClosed);
  }, []);

  function returnToMainMenu({ reconnect = true } = {}) {
    document.body.style.cursor = "default";
    setLocation("/");
    if (reconnect) {
      connectSocket();
    }
  }

  function confirmLeave() {
    trackEvent({ surface, action: isHost ? 'close_room' : 'leave_room', category: 'ui' })
    if (isHost) {
      socket.emit("closeRoom", (response) => {
        if (response === "success") {
          setConfirmationOpen(false);
          returnToMainMenu();
        }
      });
      return;
    }

    socket.emit("leaveRoom", (response) => {
      if (response === "success") {
        setConfirmationOpen(false);
        localStorage.removeItem("yootGame");
        setGamePhase("");
        disconnectSocket();
        returnToMainMenu({ reconnect: false });
      }
    });
  }

  const modalOpen = confirmationOpen || roomClosed;
  const modalPosition = roomClosed ? roomClosedModalPosition : confirmationModalPosition;

  if (!modalOpen) return null;

  return <group>
    <mesh
      name="main-menu-modal-shade"
      position={[0, 8, 7]}
      onPointerDown={(event) => event.stopPropagation()}
      onPointerUp={(event) => event.stopPropagation()}
    >
      <boxGeometry args={[100, 0.01, 100]} />
      <meshStandardMaterial color="black" transparent opacity={0.5} />
    </mesh>
    {roomClosed
      ? <group scale={roomClosedModalScale}><RoomClosedModal position={roomClosedModalPosition} font={font} onReturn={returnToMainMenu} /></group>
      : <group scale={confirmationModalScale}><ConfirmationModal isHost={isHost} position={confirmationModalPosition} font={font} onCancel={() => {
        setConfirmationOpen(false)
      }} onConfirm={confirmLeave} /></group>}
  </group>;
}